import { ADMIN_ROUTE_PATHS } from 'core/constants/admin-route-paths';
import { getNavbarHubItems } from 'core/constants/nav-navbar-by-role';
import { Role } from 'core/models/role.model';

/**
 * Prefixos de rota permitidos por perfil SGE (ACL do admin).
 * Alinhado ao nav real (sem comingSoon) - deep links fora da lista vão para `/`.
 */
const ROUTE_PREFIXES_BY_ROLE: Record<Role, readonly string[]> = {
  SUPER_ADMIN: [
    '/',
    '/setup-wizard',
    '/institutions',
    '/users',
    '/transfers',
    '/settings',
    '/profile',
    '/system-logs',
    '/notifications',
    '/tickets',
    '/search',
  ],
  PLATFORM_ADMIN: [
    '/',
    '/setup-wizard',
    '/institutions',
    '/users',
    '/transfers',
    '/settings',
    '/profile',
    '/system-logs',
    '/notifications',
    '/tickets',
    '/search',
  ],
  SUPPORT: [
    '/',
    '/setup-wizard',
    '/institutions',
    '/settings',
    '/profile',
    '/notifications',
    '/tickets',
    '/search',
  ],
  INSTITUTION_ADMIN: [
    '/',
    '/setup-wizard',
    '/institutions',
    '/users',
    '/students',
    '/teachers',
    '/support-staff',
    '/classes',
    '/enrollments',
    '/transfers',
    '/class-teachers',
    '/academic-courses',
    '/subjects',
    '/school-calendars',
    '/guardians',
    '/student-guardians',
    '/pickup-desk',
    '/school-access',
    '/school-exit',
    '/attendances',
    '/grades',
    '/report-cards',
    '/reports',
    '/school-documents',
    '/census',
    '/guardian-consents',
    '/class-diary',
    '/lesson-plans',
    '/class-schedules',
    '/classroom-seating',
    '/settings',
    '/profile',
    '/system-logs',
    '/notifications',
    '/tickets',
    '/search',
  ],
  SECRETARY: [
    '/',
    '/setup-wizard',
    '/users',
    '/students',
    '/teachers',
    '/support-staff',
    '/classes',
    '/enrollments',
    '/transfers',
    '/class-teachers',
    '/school-calendars',
    '/guardians',
    '/student-guardians',
    '/pickup-desk',
    '/school-access',
    '/school-exit',
    '/attendances',
    '/grades',
    '/report-cards',
    '/reports',
    '/school-documents',
    '/census',
    '/guardian-consents',
    '/class-diary',
    '/lesson-plans',
    '/class-schedules',
    '/classroom-seating',
    '/settings',
    '/profile',
    '/notifications',
    '/tickets',
    '/search',
  ],
  TEACHER: [
    '/',
    '/classes/list',
    '/classes/details',
    '/courses/list',
    '/school-calendars/list',
    '/school-calendars/details',
    '/attendances',
    '/grades',
    '/report-cards',
    '/reports',
    '/class-diary',
    '/lesson-plans',
    '/class-schedules',
    '/classroom-seating',
    '/profile',
    '/notifications',
    '/tickets',
    '/search',
  ],
  STUDENT: [
    '/',
    '/classes/list',
    '/classes/details',
    '/courses/list',
    '/school-calendars/list',
    '/school-calendars/details',
    '/report-cards',
    '/class-schedules',
    '/profile',
    '/notifications',
    '/tickets',
    '/search',
  ],
  GUARDIAN: [
    '/',
    '/student-guardians',
    '/enrollments',
    '/school-calendars/list',
    '/school-calendars/details',
    '/pickup-desk',
    '/school-access',
    '/school-exit',
    '/report-cards',
    '/school-documents',
    '/guardian-consents',
    '/class-schedules',
    '/profile',
    '/notifications',
    '/tickets',
    '/search',
  ],
  FINANCE: [
    '/',
    '/setup-wizard',
    '/enrollments',
    '/users',
    '/settings',
    '/profile',
    '/notifications',
    '/tickets',
    '/search',
  ],
  TRANSPORT_OPERATOR: [
    '/',
    '/setup-wizard',
    '/users',
    '/settings',
    '/profile',
    '/notifications',
    '/tickets',
    '/search',
  ],
};

function normalizePath(url: string): string {
  const path = url.split('?')[0]?.split('#')[0] ?? '/';
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path || '/';
}

export function allowedRoutePrefixesForRole(
  role: Role | null | undefined,
): readonly string[] {
  if (!role) {
    return [];
  }
  return ROUTE_PREFIXES_BY_ROLE[role] ?? [];
}

export function roleCanAccessPath(
  role: Role | null | undefined,
  url: string,
): boolean {
  if (!role) {
    return false;
  }

  const path = normalizePath(url);

  if (path === ADMIN_ROUTE_PATHS.createHub) {
    return getNavbarHubItems(role, 'create').length > 0;
  }

  if (path === ADMIN_ROUTE_PATHS.linkHub) {
    return getNavbarHubItems(role, 'link').length > 0;
  }

  if (path === ADMIN_ROUTE_PATHS.listHub) {
    return getNavbarHubItems(role, 'list').length > 0;
  }

  if (path === ADMIN_ROUTE_PATHS.diaryHub) {
    return getNavbarHubItems(role, 'diary').length > 0;
  }

  if (path === ADMIN_ROUTE_PATHS.trashHub) {
    return getNavbarHubItems(role, 'trash').length > 0;
  }

  const prefixes = ROUTE_PREFIXES_BY_ROLE[role];
  if (!prefixes) {
    return false;
  }

  return prefixes.some((prefix) => {
    if (prefix === '/') {
      return path === '/';
    }
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}
